import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { Badge } from "../../design/badge";
import { Button } from "../../design/button";
import ArtistAvatar from "../../components/ArtistAvatar";
import { useAuth } from "../../contexts/AuthContext";
import { Artist, MarketMetrics } from "../../types";
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  PaintBrushIcon,
  CalendarIcon,
  ArrowLeftIcon,
} from "@heroicons/react/20/solid";

const ArtistDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [artist, setArtist] = useState<Artist | null>(null);
  const [metrics, setMetrics] = useState<MarketMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArtistData = async () => {
      if (!id || !user) return;

      setLoading(true);
      try {
        const token = await user.getIdToken();

        // Fetch artist details
        const artistRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/v1/artists/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setArtist(artistRes.data.data);

        // Fetch market metrics
        try {
          const metricsRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v1/price_database`,
            {
              params: { artist: id, include_analytics: "true" },
            }
          );
          if (metricsRes.data.analytics) {
            setMetrics({
              totalSales: metricsRes.data.stats?.count || 0,
              averagePrice: metricsRes.data.stats?.avgPrice || 0,
              medianPrice: metricsRes.data.stats?.medianPrice || 0,
              highestPrice: metricsRes.data.stats?.priceRange?.max || 0,
              cagr: metricsRes.data.analytics.performance?.cagr,
              investmentGrade: metricsRes.data.analytics.summary?.investmentGrade,
              riskLevel: metricsRes.data.analytics.risk?.riskLevel,
              liquidityScore: metricsRes.data.analytics.market?.liquidityScore,
            });
          }
        } catch (err) {
          console.error("Failed to fetch metrics:", err);
        }
      } catch (err) {
        setError("Failed to load artist details");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchArtistData();
  }, [id, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text>Loading...</Text>
      </div>
    );
  }

  if (error || !artist) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Text className="text-red-600">{error || "Artist not found"}</Text>
        <Button onClick={() => navigate("/auctionRecords")}>
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Search
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-zinc-950">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Button
          plain
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Artist Header */}
        <div className="p-8 mb-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
          <div className="flex items-start gap-6">
            <ArtistAvatar artist={artist} size="xl" />
            <div className="flex-1">
              <Heading className="mb-2">{artist.name}</Heading>
              <div className="flex flex-wrap gap-2 mb-4">
                {artist.nationality && (
                  <Badge color="blue">{artist.nationality}</Badge>
                )}
                {artist.birthYear && (
                  <Badge color="zinc">
                    <CalendarIcon className="w-3 h-3 mr-1" />
                    {artist.birthYear}
                    {artist.deathYear && ` - ${artist.deathYear}`}
                  </Badge>
                )}
              </div>
              {artist.biography && (
                <Text className="text-gray-600 dark:text-gray-400">{artist.biography}</Text>
              )}
            </div>
          </div>
        </div>

        {/* Market Metrics */}
        {metrics && (
          <>
            <Subheading className="mb-4">Market Performance</Subheading>
            <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="p-6 bg-white rounded-lg shadow-md dark:bg-zinc-900">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/20">
                    <ChartBarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <Text className="text-sm text-gray-500">Total Sales</Text>
                </div>
                <Text className="text-3xl font-bold">{metrics.totalSales.toLocaleString()}</Text>
              </div>

              <div className="p-6 bg-white rounded-lg shadow-md dark:bg-zinc-900">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-green-50 dark:bg-green-950/20">
                    <CurrencyDollarIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <Text className="text-sm text-gray-500">Average Price</Text>
                </div>
                <Text className="text-3xl font-bold">
                  ${(metrics.averagePrice / 100).toLocaleString()}
                </Text>
              </div>

              {metrics.cagr !== undefined && (
                <div className="p-6 bg-white rounded-lg shadow-md dark:bg-zinc-900">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-950/20">
                      <ArrowTrendingUpIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <Text className="text-sm text-gray-500">CAGR</Text>
                  </div>
                  <Text className="text-3xl font-bold">
                    {(metrics.cagr * 100).toFixed(1)}%
                  </Text>
                </div>
              )}

              {metrics.investmentGrade !== undefined && (
                <div className="p-6 bg-white rounded-lg shadow-md dark:bg-zinc-900">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-950/20">
                      <PaintBrushIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <Text className="text-sm text-gray-500">Investment Grade</Text>
                  </div>
                  <Text className="text-3xl font-bold">{metrics.investmentGrade}/100</Text>
                </div>
              )}
            </div>

            {/* Additional Metrics */}
            <div className="p-6 mb-6 bg-white rounded-lg shadow-md dark:bg-zinc-900">
              <Subheading className="mb-4">Additional Details</Subheading>
              <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                <div>
                  <Text className="text-sm text-gray-500">Median Price</Text>
                  <Text className="text-lg font-semibold">
                    ${(metrics.medianPrice / 100).toLocaleString()}
                  </Text>
                </div>
                <div>
                  <Text className="text-sm text-gray-500">Highest Price</Text>
                  <Text className="text-lg font-semibold">
                    ${(metrics.highestPrice / 100).toLocaleString()}
                  </Text>
                </div>
                {metrics.riskLevel && (
                  <div>
                    <Text className="text-sm text-gray-500">Risk Level</Text>
                    <Badge color={metrics.riskLevel === "Low" ? "green" : metrics.riskLevel === "Medium" ? "yellow" : "red"}>
                      {metrics.riskLevel}
                    </Badge>
                  </div>
                )}
                {metrics.liquidityScore !== undefined && (
                  <div>
                    <Text className="text-sm text-gray-500">Liquidity Score</Text>
                    <Text className="text-lg font-semibold">{metrics.liquidityScore.toFixed(1)}/10</Text>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex gap-4">
          <Button href={`/auctionRecords?artist=${id}`} color="blue">
            View Auction Records
          </Button>
          <Button href={`/valuation?artist=${id}`} outline>
            Get Valuation
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ArtistDetailPage;
